import React, { useState, useEffect, useRef } from 'react';
import { Navigate, Link } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import { 
  BookOpen, 
  Plus, 
  Trash2, 
  Brain,
  Save,
  X,
  AlertTriangle,
  Shield,
  Archive,
  ArchiveRestore,
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import ConfirmDialog from '@/components/common/ConfirmDialog';

interface Stage {
  id: number;
  subject_id: number;
  name: string;
  completion_score_threshold: number;
  stage_order: number;
  created_at: string;
  updated_at: string;
  questions?: Question[];
}

interface Question {
  id: number;
  stage_id: number;
  question_text: string;
  question_order: number;
  time_limit_seconds?: number;
  created_at: string;
  updated_at: string;
  answers?: Answer[];
}

interface Answer {
  id: number;
  question_id: number;
  answer_text: string;
  is_correct: boolean;
  explanation: string | null;
  created_at: string;
  updated_at: string;
}

interface Subject {
  id: number;
  name: string;
  summary: string;
  knowledge_base_prompt: string;
  created_by: number;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  creator_name: string;
  stage_count: number;
  question_count: number;
  stages?: Stage[];
}

interface CreateSubjectForm {
  name: string;
  summary: string;
  knowledgeBasePrompt: string;
}

interface CreateStageForm {
  name: string;
  completionScoreThreshold: number;
}

interface CreateQuestionForm {
  questionText: string;
  answers: Array<{
    text: string;
    isCorrect: boolean;
    explanation: string;
  }>;
}

interface TempQuestion {
  tempId: string;
  questionText: string;
  timeLimitSeconds?: number;
  answer: {
    tempId: string;
    text: string;
    isCorrect: boolean;
  };
}

type PendingTempRemoval =
  | { type: 'stage'; stageIndex: number; name: string }
  | { type: 'question'; stageIndex: number; questionIndex: number };

export const SubjectEditorPage: React.FC = () => {
  const { user, isAuthenticated } = useAuthStore();
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState<Subject | null>(null);
  const [createForm, setCreateForm] = useState<CreateSubjectForm>({
    name: '',
    summary: '',
    knowledgeBasePrompt: ''
  });
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [showStageModal, setShowStageModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);
  const [selectedStage, setSelectedStage] = useState<Stage | null>(null);
  const [createStageForm, setCreateStageForm] = useState<CreateStageForm>({
    name: '',
    completionScoreThreshold: 70
  });
  const [createQuestionForm, setCreateQuestionForm] = useState<CreateQuestionForm>({
    questionText: '',
    answers: [
      { text: '', isCorrect: false, explanation: '' },
      { text: '', isCorrect: false, explanation: '' },
      { text: '', isCorrect: false, explanation: '' },
      { text: '', isCorrect: false, explanation: '' }
    ]
  });

  const [tempStages, setTempStages] = useState<{ tempId: string; name: string; completion_score_threshold: number; stage_order: number; questions: TempQuestion[] }[]>([]);
  const [tempStageCounter, setTempStageCounter] = useState(0);
  const [tempQuestionCounter, setTempQuestionCounter] = useState(0);
  const [tempAnswerCounter, setTempAnswerCounter] = useState(0);
  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active');
  const [importing, setImporting] = useState(false);
  const [pendingTempRemoval, setPendingTempRemoval] = useState<PendingTempRemoval | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isFetching = useRef(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubjects();
    }
  }, [isAuthenticated]);

  const fetchSubjects = async () => {
    if (isFetching.current) {
      return;
    }

    try {
      isFetching.current = true;
      const token = localStorage.getItem('teaching_app_token');
      
      const response = await fetch('/api/subjects', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.subjects && Array.isArray(data.subjects)) {
          setSubjects(data.subjects);
        } else if (Array.isArray(data)) {
          setSubjects(data);
        } else {
          setSubjects([]);
        }
      } else if (response.status === 429) {
        console.warn('Rate limited (429), please wait a moment and refresh');
        setSubjects([]);
      } else {
        setSubjects([]);
      }
    } catch {
      setSubjects([]);
    } finally {
      isFetching.current = false;
      setLoading(false);
    }
  };

  const handleExportSubject = async (subject: Subject) => {
    try {
      const token = localStorage.getItem('teaching_app_token');
      const response = await fetch(`/api/subjects/${subject.id}/export`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to export subject:', errorText);
        alert('Failed to export subject as CSV.');
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = subject.name.replace(/[^a-z0-9_-]+/gi, '_').toLowerCase();
      link.download = `subject_${safeName || subject.id}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting subject:', error);
      alert('Error exporting subject as CSV.');
    }
  };

  // Parse CSV and extract subject data
  const parseCsvToFormData = (csvText: string) => {
    if (!csvText.trim()) {
      throw new Error('CSV file is empty.');
    }

    // Simple CSV parser (handles quoted fields with escaped quotes)
    const parseCsv = (text: string): string[][] => {
      const rows: string[][] = [];
      let currentRow: string[] = [];
      let currentField = '';
      let inQuotes = false;

      for (let i = 0; i < text.length; i++) {
        const char = text[i];
        const next = text[i + 1];

        if (char === '"') {
          if (inQuotes && next === '"') {
            currentField += '"';
            i++; // Skip next quote
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          currentRow.push(currentField);
          currentField = '';
        } else if ((char === '\n' || char === '\r') && !inQuotes) {
          if (char === '\r' && next === '\n') {
            i++; // Skip \n after \r
          }
          if (currentField || currentRow.length > 0) {
            currentRow.push(currentField);
            rows.push(currentRow);
            currentRow = [];
            currentField = '';
          }
        } else {
          currentField += char;
        }
      }

      // Add last field and row
      if (currentField || currentRow.length > 0) {
        currentRow.push(currentField);
        rows.push(currentRow);
      }

      return rows;
    };

    const rows = parseCsv(csvText);
    if (rows.length < 2) {
      throw new Error('CSV must contain a header and at least one data row');
    }

    const headerRow = rows[0];
    if (!headerRow) {
      throw new Error('CSV must contain a header row');
    }

    const header = headerRow.map(h => h.trim().toLowerCase());
    const dataRows = rows.slice(1);

    // Find column indices
    const getIdx = (name: string) => {
      const idx = header.indexOf(name);
      if (idx === -1) {
        throw new Error(`Missing required column: ${name}`);
      }
      return idx;
    };

    const getIdxOpt = (name: string) => header.indexOf(name);

    const subjectNameIdx = getIdx('subject_name');
    const subjectSummaryIdx = getIdx('subject_summary');
    const knowledgeBasePromptIdx = getIdxOpt('knowledge_base_prompt');
    const stageOrderIdx = getIdx('stage_order');
    const stageNameIdx = getIdx('stage_name');
    const questionOrderIdx = getIdx('question_order');
    const questionTextIdx = getIdx('question_text');
    const timeLimitIdx = getIdx('time_limit_seconds');
    const answerTextIdx = getIdxOpt('answer_text');
    const answerIsCorrectIdx = getIdxOpt('answer_is_correct');
    const answerExplanationIdx = getIdxOpt('answer_explanation');

    // Extract subject info from first row
    const firstRow = dataRows[0];
    if (!firstRow) {
      throw new Error('CSV must contain at least one data row');
    }

    const subjectName = (firstRow[subjectNameIdx] || '').trim();
    const subjectSummary = (firstRow[subjectSummaryIdx] || '').trim();
    const knowledgeBasePrompt = knowledgeBasePromptIdx >= 0 
      ? (firstRow[knowledgeBasePromptIdx] || '').trim() 
      : '';

    if (!subjectName) {
      throw new Error('subject_name is required in CSV');
    }

    // Group data by stage
    const stageMap = new Map<string, {
      stageOrder: number;
      stageName: string;
      completionThreshold: number;
      questions: Array<{
        questionOrder: number;
        questionText: string;
        timeLimitSeconds: number;
        answerText: string;
        answerIsCorrect: boolean;
        answerExplanation: string;
      }>;
    }>();

    let lastStageName = '';
    let lastStageOrder = 0;

    for (const row of dataRows) {
      let stageName = (row[stageNameIdx] || '').trim();
      const stageOrderRaw = row[stageOrderIdx] || '';
      const questionText = (row[questionTextIdx] || '').trim();
      const questionOrderRaw = row[questionOrderIdx] || '';
      const timeLimitRaw = row[timeLimitIdx] || '';

      // Reuse last stage name if empty
      if (!stageName && lastStageName) {
        stageName = lastStageName;
      }
      if (!stageName) {
        continue; // Skip rows without stage name
      }
      if (!questionText) {
        continue; // Skip rows without question text
      }

      const stageOrder = stageOrderRaw ? parseInt(stageOrderRaw, 10) : lastStageOrder;
      const questionOrder = questionOrderRaw ? parseInt(questionOrderRaw, 10) : 0;
      const timeLimitSeconds = timeLimitRaw ? parseInt(timeLimitRaw, 10) : 120;

      const answerText = answerTextIdx >= 0 ? (row[answerTextIdx] || '').trim() : '';
      const answerIsCorrectRaw = answerIsCorrectIdx >= 0 ? (row[answerIsCorrectIdx] || '').trim() : '';
      const answerIsCorrect = answerIsCorrectRaw === '' ? true : (parseInt(answerIsCorrectRaw, 10) === 1);
      const answerExplanation = answerExplanationIdx >= 0 ? (row[answerExplanationIdx] || '').trim() : '';

      if (!stageMap.has(stageName)) {
        stageMap.set(stageName, {
          stageOrder,
          stageName,
          completionThreshold: 70, // Default, not in CSV
          questions: []
        });
      }

      const stage = stageMap.get(stageName)!;
      stage.questions.push({
        questionOrder,
        questionText,
        timeLimitSeconds,
        answerText,
        answerIsCorrect,
        answerExplanation
      });

      lastStageName = stageName;
      lastStageOrder = stageOrder;
    }

    // Convert to tempStages format
    const stages = Array.from(stageMap.values())
      .sort((a, b) => a.stageOrder - b.stageOrder)
      .map((stage, index) => ({
        tempId: `temp-stage-${tempStageCounter + index}`,
        name: stage.stageName,
        completion_score_threshold: stage.completionThreshold,
        stage_order: index,
        questions: stage.questions
          .sort((a, b) => a.questionOrder - b.questionOrder)
          .map((q, qIndex) => ({
            tempId: `temp-question-${tempQuestionCounter + index * 100 + qIndex}`,
            questionText: q.questionText,
            timeLimitSeconds: q.timeLimitSeconds,
            answer: {
              tempId: `temp-answer-${tempAnswerCounter + index * 100 + qIndex}`,
              text: q.answerText,
              isCorrect: q.answerIsCorrect
            }
          }))
      }));

    return {
      subjectName,
      subjectSummary,
      knowledgeBasePrompt,
      stages
    };
  };

  const importCsvText = (csvText: string) => {
    if (!csvText.trim()) {
      alert('Selected CSV file is empty.');
      return;
    }

    try {
      setImporting(true);
      const parsed = parseCsvToFormData(csvText);

      // Populate form fields
      setCreateForm({
        name: parsed.subjectName,
        summary: parsed.subjectSummary,
        knowledgeBasePrompt: parsed.knowledgeBasePrompt
      });

      // Populate stages
      if (parsed.stages.length > 0) {
        setTempStages(parsed.stages);
        setTempStageCounter(prev => prev + parsed.stages.length);
        setTempQuestionCounter(prev => prev + parsed.stages.reduce((sum, s) => sum + s.questions.length, 0));
        setTempAnswerCounter(prev => prev + parsed.stages.reduce((sum, s) => sum + s.questions.length, 0));
      }

      // Open/create modal if not already open
      if (!showCreateModal && !editingSubject) {
        setShowCreateModal(true);
      }

      alert('CSV data loaded into form. Review and click "Create Subject" to save.');
    } catch (error) {
      console.error('Error parsing CSV:', error);
      alert(error instanceof Error ? error.message : 'Error parsing CSV file.');
    } finally {
      setImporting(false);
    }
  };

  const handleImportFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      importCsvText(text);
      // reset input so selecting the same file again still triggers change
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };
    reader.readAsText(file);
  };

  const handleCreateSubject = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate questions and answers before submission
    const validationErrors = [];
    for (const stage of tempStages) {
      for (const question of stage.questions) {
        if (!question.questionText || question.questionText.length < 3) {
          validationErrors.push(`Question "${question.questionText || ''}" must be at least 3 characters long`);
        }
        if (!question.answer?.text || question.answer.text.length < 1) {
          validationErrors.push(`Answer for question "${question.questionText || ''}" cannot be empty`);
        }
      }
    }
    
    if (validationErrors.length > 0) {
      alert(`Please fix the following validation errors:\n\n${validationErrors.join('\n')}`);
      return;
    }
    
    const requestBody = {
      name: createForm.name?.trim() || '',
      summary: (createForm.summary ?? '').trim(),
      knowledgeBasePrompt: (createForm.knowledgeBasePrompt ?? '').trim()
    };
    
    try {
      const response = await fetch('/api/subjects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        const newSubject = data.subject;
        
        for (const stage of tempStages) {
          const stageResponse = await fetch(`/api/subjects/${newSubject.id}/stages`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
            },
            body: JSON.stringify({
              name: stage.name,
              completionScoreThreshold: stage.completion_score_threshold
            })
          });

          if (stageResponse.ok) {
            const stageData = await stageResponse.json();
            
            for (const question of stage.questions) {
              const questionResponse = await fetch(`/api/subjects/${newSubject.id}/stages/${stageData.stage.id}/questions`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
                },
                body: JSON.stringify({
                  questionText: question.questionText,
                  timeLimitSeconds: question.timeLimitSeconds || 120
                })
              });

              if (questionResponse.ok) {
                const questionData = await questionResponse.json();
                
                if (question.answer?.text?.trim()) {
                  const answerResponse = await fetch(`/api/subjects/${newSubject.id}/questions/${questionData.question.id}/answers`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
                    },
                    body: JSON.stringify({
                      answerText: question.answer.text,
                      isCorrect: true,
                      explanation: null
                    })
                  });

                  if (!answerResponse.ok) {
                    const errorData = await answerResponse.json();
                    console.error('Failed to create answer:', errorData);
                  }
                }
              } else {
                console.error('Failed to create question:', await questionResponse.json());
              }
            }
          } else {
            console.error('Failed to create stage:', await stageResponse.json());
          }
        }
        
        // Refresh subjects to get the updated data with stages and questions
        await fetchSubjects();
        setShowCreateModal(false);
        setCreateForm({
          name: '',
          summary: '',
          knowledgeBasePrompt: ''
        });
        setTempStages([]);
        setTempStageCounter(0);
        setTempQuestionCounter(0);
        setTempAnswerCounter(0);
      } else {
        const error = await response.json();
        
        if (error.details && error.details.length > 0) {
          const errorMessages = error.details.map((detail: { field: string; message: string }) => `${detail.field}: ${detail.message}`).join('\n');
          alert(`Validation failed:\n${errorMessages}`);
        } else {
          alert(error.error || 'Failed to create subject');
        }
      }
    } catch (error) {
      console.error('Failed to create subject:', error);
      alert('Failed to create subject');
    }
  };

  const handleUpdateSubject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSubject) return;
    
    // Validate questions and answers before submission
    const validationErrors = [];
    for (const stage of tempStages) {
      for (const question of stage.questions) {
        if (!question.questionText || question.questionText.length < 3) {
          validationErrors.push(`Question "${question.questionText || ''}" must be at least 3 characters long`);
        }
        if (!question.answer?.text || question.answer.text.length < 1) {
          validationErrors.push(`Answer for question "${question.questionText || ''}" cannot be empty`);
        }
      }
    }
    
    if (validationErrors.length > 0) {
      alert(`Please fix the following validation errors:\n\n${validationErrors.join('\n')}`);
      return;
    }
    
    const requestBody = {
      name: createForm.name?.trim() || '',
      summary: (createForm.summary ?? '').trim(),
      knowledgeBasePrompt: (createForm.knowledgeBasePrompt ?? '').trim()
    };
    
    try {
      const response = await fetch(`/api/subjects/${editingSubject.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        for (const stage of tempStages) {
          const isExistingStage = stage.tempId.startsWith('existing-stage-');
          
          if (isExistingStage) {
            const stageId = stage.tempId.replace('existing-stage-', '');
            const stageResponse = await fetch(`/api/subjects/${editingSubject.id}/stages/${stageId}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
              },
              body: JSON.stringify({
                name: stage.name,
                completionScoreThreshold: stage.completion_score_threshold
              })
            });

            if (stageResponse.ok) {
              for (const question of stage.questions) {
                const isExistingQuestion = question.tempId.startsWith('existing-question-');
                
                if (isExistingQuestion) {
                  const questionId = question.tempId.replace('existing-question-', '');
                  const questionResponse = await fetch(`/api/subjects/${editingSubject.id}/stages/${stageId}/questions/${questionId}`, {
                    method: 'PUT',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
                    },
                    body: JSON.stringify({
                      questionText: question.questionText,
                      timeLimitSeconds: question.timeLimitSeconds || 120
                    })
                  });

                  if (questionResponse.ok) {
                    if (question.answer?.text?.trim()) {
                      const answerId = question.answer.tempId.replace('existing-answer-', '');
                      if (answerId !== 'new') {
                        const answerResponse = await fetch(`/api/subjects/${editingSubject.id}/questions/${questionId}/answers/${answerId}`, {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
                          },
                          body: JSON.stringify({
                            answerText: question.answer.text,
                            isCorrect: true,
                            explanation: null
                          })
                        });

                        if (!answerResponse.ok) {
                          console.error('Failed to update answer:', await answerResponse.json());
                        }
                      }
                    }
                  } else {
                    console.error('Failed to update question:', await questionResponse.json());
                  }
                } else {
                  const questionResponse = await fetch(`/api/subjects/${editingSubject.id}/stages/${stageId}/questions`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
                    },
                    body: JSON.stringify({
                      questionText: question.questionText,
                      timeLimitSeconds: question.timeLimitSeconds || 120
                    })
                  });

                  if (questionResponse.ok) {
                    const questionData = await questionResponse.json();
                    
                    if (question.answer?.text?.trim()) {
                      const answerResponse = await fetch(`/api/subjects/${editingSubject.id}/questions/${questionData.question.id}/answers`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
                        },
                        body: JSON.stringify({
                          answerText: question.answer.text,
                          isCorrect: true,
                          explanation: null
                        })
                      });

                      if (!answerResponse.ok) {
                        const errorData = await answerResponse.json();
                        console.error('Failed to create answer:', errorData);
                      }
                    }
                  } else {
                    console.error('Failed to create question:', await questionResponse.json());
                  }
                }
              }
            } else {
              console.error('Failed to update stage:', await stageResponse.json());
            }
          } else {
            const stageResponse = await fetch(`/api/subjects/${editingSubject.id}/stages`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
              },
              body: JSON.stringify({
                name: stage.name,
                completionScoreThreshold: stage.completion_score_threshold
              })
            });

            if (stageResponse.ok) {
              const stageData = await stageResponse.json();
              
              for (const question of stage.questions) {
                const questionResponse = await fetch(`/api/subjects/${editingSubject.id}/stages/${stageData.stage.id}/questions`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
                  },
                  body: JSON.stringify({
                    questionText: question.questionText
                  })
                });

                if (questionResponse.ok) {
                  const questionData = await questionResponse.json();
                  
                  if (question.answer?.text?.trim()) {
                    const answerResponse = await fetch(`/api/subjects/${editingSubject.id}/questions/${questionData.question.id}/answers`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
                      },
                      body: JSON.stringify({
                        answerText: question.answer.text,
                        isCorrect: true,
                        explanation: null
                      })
                    });

                    if (!answerResponse.ok) {
                      const errorData = await answerResponse.json();
                      console.error('Failed to create answer:', errorData);
                    }
                  }
                } else {
                  console.error('Failed to create question:', await questionResponse.json());
                }
              }
            } else {
              console.error('Failed to create stage:', await stageResponse.json());
            }
          }
        }
        
        // Refresh subjects to get the updated data
        await fetchSubjects();
        setEditingSubject(null);
        setCreateForm({
          name: '',
          summary: '',
          knowledgeBasePrompt: ''
        });
        setTempStages([]);
        setTempStageCounter(0);
        setTempQuestionCounter(0);
        setTempAnswerCounter(0);
      } else {
        const error = await response.json();
        
        if (error.details && error.details.length > 0) {
          const errorMessages = error.details.map((detail: { field: string; message: string }) => `${detail.field}: ${detail.message}`).join('\n');
          alert(`Validation failed:\n${errorMessages}`);
        } else {
          alert(error.error || 'Failed to update subject');
        }
      }
    } catch (error) {
      console.error('Failed to update subject:', error);
      alert('Failed to update subject');
    }
  };

  const handleDeleteSubject = async (subjectId: number) => {
    try {
      const response = await fetch(`/api/subjects/${subjectId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
        }
      });

      if (response.ok) {
        setSubjects(prev => prev.filter(subject => subject.id !== subjectId));
        setDeleteConfirm(null);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to delete subject');
      }
    } catch (error) {
      console.error('Failed to delete subject:', error);
      alert('Failed to delete subject');
    }
  };

  const handleArchiveSubject = async (subjectId: number) => {
    try {
      const response = await fetch(`/api/subjects/${subjectId}/archive`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
        },
        body: JSON.stringify({ isActive: false })
      });

      if (response.ok) {
        setSubjects(prev => prev.map(subject => 
          subject.id === subjectId 
            ? { ...subject, is_active: false }
            : subject
        ));
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to archive subject');
      }
    } catch (error) {
      console.error('Failed to archive subject:', error);
      alert('Failed to archive subject');
    }
  };

  const handleUnarchiveSubject = async (subjectId: number) => {
    try {
      const response = await fetch(`/api/subjects/${subjectId}/archive`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
        },
        body: JSON.stringify({ isActive: true })
      });

      if (response.ok) {
        setSubjects(prev => prev.map(subject => 
          subject.id === subjectId 
            ? { ...subject, is_active: true }
            : subject
        ));
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to unarchive subject');
      }
    } catch (error) {
      console.error('Failed to unarchive subject:', error);
      alert('Failed to unarchive subject');
    }
  };

  const openEditModal = async (subject: Subject) => {
    setEditingSubject(subject);
    setCreateForm({
      name: subject.name || '',
      summary: subject.summary || '',
      knowledgeBasePrompt: subject.knowledge_base_prompt || ''
    });
    
    // Load subject details with stages and questions
    try {
      const token = localStorage.getItem('teaching_app_token');
      const response = await fetch(`/api/subjects/${subject.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const subjectWithDetails = data.subject;

        // Update form with the most up-to-date knowledge base prompt
        setCreateForm({
          name: subjectWithDetails.name || '',
          summary: subjectWithDetails.summary || '',
          knowledgeBasePrompt: subjectWithDetails.knowledge_base_prompt || ''
        });
        
        // Convert existing stages to tempStages format
        const existingStages = subjectWithDetails.stages?.map((stage: any, index: number) => ({
          tempId: `existing-stage-${stage.id}`,
          name: stage.name,
          completion_score_threshold: stage.completion_score_threshold,
          stage_order: index,
          questions: stage.questions?.map((question: any) => ({
            tempId: `existing-question-${question.id}`,
            questionText: question.question_text,
            timeLimitSeconds: question.time_limit_seconds || 120,
            answer: {
              tempId: `existing-answer-${question.answers?.[0]?.id || 'new'}`,
              text: question.answers?.[0]?.answer_text || '',
              isCorrect: true
            }
          })) || []
        })) || [];
        
        setTempStages(existingStages);
        setTempStageCounter(0);
        setTempQuestionCounter(0);
        setTempAnswerCounter(0);
      }
    } catch (error) {
      console.error('Failed to load subject details for editing:', error);
    }
  };

  const closeModals = () => {
    setShowCreateModal(false);
    setEditingSubject(null);
    setCreateForm({
      name: '',
      summary: '',
      knowledgeBasePrompt: ''
    });
    setTempStages([]);
    setTempStageCounter(0);
    setTempQuestionCounter(0);
    setTempAnswerCounter(0);
  };

  const closeStageModal = () => {
    setShowStageModal(false);
    setSelectedSubject(null);
    setCreateStageForm({
      name: '',
      completionScoreThreshold: 70
    });
  };

  const closeQuestionModal = () => {
    setShowQuestionModal(false);
    setSelectedStage(null);
    setCreateQuestionForm({
      questionText: '',
      answers: [
        { text: '', isCorrect: false, explanation: '' },
        { text: '', isCorrect: false, explanation: '' },
        { text: '', isCorrect: false, explanation: '' },
        { text: '', isCorrect: false, explanation: '' }
      ]
    });
  };

  const removeTempStage = (stageIndex: number) => {
    setTempStages(prev => prev.filter((_, index) => index !== stageIndex));
  };

  const removeTempQuestion = (stageIndex: number, questionIndex: number) => {
    setTempStages(prev => prev.map((stage, index) => (
      index === stageIndex
        ? { ...stage, questions: stage.questions.filter((_, qIndex) => qIndex !== questionIndex) }
        : stage
    )));
  };

  const confirmTempRemoval = () => {
    if (!pendingTempRemoval) {
      return;
    }

    if (pendingTempRemoval.type === 'stage') {
      removeTempStage(pendingTempRemoval.stageIndex);
    } else {
      removeTempQuestion(pendingTempRemoval.stageIndex, pendingTempRemoval.questionIndex);
    }

    setPendingTempRemoval(null);
  };

  const handleCreateStage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSubject) return;

    try {
      const token = localStorage.getItem('teaching_app_token');
      const response = await fetch(`/api/subjects/${selectedSubject.id}/stages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: createStageForm.name,
          completionScoreThreshold: createStageForm.completionScoreThreshold
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSubjects(prev => prev.map(subject => 
          subject.id === selectedSubject.id 
            ? { 
                ...subject, 
                stages: [...(subject.stages || []), data.stage],
                stage_count: (subject.stage_count || 0) + 1
              }
            : subject
        ));
        closeStageModal();
      } else {
        const errorData = await response.json();
        console.error('Failed to create stage:', errorData);
      }
    } catch (error) {
      console.error('Failed to create stage:', error);
    }
  };

  const handleCreateQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStage || !selectedSubject) return;

    try {
      const token = localStorage.getItem('teaching_app_token');
      
      // First, create the question
      const questionResponse = await fetch(`/api/subjects/${selectedSubject.id}/stages/${selectedStage.id}/questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          questionText: createQuestionForm.questionText
        })
      });

      if (!questionResponse.ok) {
        const errorData = await questionResponse.json();
        console.error('Failed to create question:', errorData);
        return;
      }

      const questionData = await questionResponse.json();
      const question = questionData.question;

      // Then, create each answer for the question
      const answerPromises = createQuestionForm.answers.map(async (answer) => {
        if (!answer.text.trim()) return null; // Skip empty answers
        
        const answerResponse = await fetch(`/api/subjects/${selectedSubject.id}/questions/${question.id}/answers`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            answerText: answer.text,
            isCorrect: answer.isCorrect,
            explanation: answer.explanation || null
          })
        });

        if (answerResponse.ok) {
          const answerData = await answerResponse.json();
          return answerData.answer;
        } else {
          console.error('Failed to create answer:', await answerResponse.json());
          return null;
        }
      });

      const answers = await Promise.all(answerPromises);
      const validAnswers = answers.filter(answer => answer !== null);

      // Update the local state with the new question and answers
      const questionWithAnswers = {
        ...question,
        answers: validAnswers
      };

      setSubjects(prev => prev.map(subject => 
        subject.id === selectedSubject.id 
          ? {
              ...subject,
              stages: (subject.stages || []).map(stage =>
                stage.id === selectedStage.id
                  ? { ...stage, questions: [...(stage.questions || []), questionWithAnswers] }
                  : stage
              ),
              question_count: (subject.question_count || 0) + 1
            }
          : subject
      ));

      closeQuestionModal();
    } catch (error) {
      console.error('Failed to create question:', error);
    }
  };

  const addAnswerField = () => {
    setCreateQuestionForm(prev => ({
      ...prev,
      answers: [...prev.answers, { text: '', isCorrect: false, explanation: '' }]
    }));
  };

  const removeAnswerField = (index: number) => {
    if (createQuestionForm.answers.length > 2) {
      setCreateQuestionForm(prev => ({
        ...prev,
        answers: prev.answers.filter((_, i) => i !== index)
      }));
    }
  };

  const updateAnswer = (index: number, field: 'text' | 'isCorrect' | 'explanation', value: string | boolean) => {
    setCreateQuestionForm(prev => ({
      ...prev,
      answers: prev.answers.map((answer, i) => 
        i === index ? { ...answer, [field]: value } : answer
      )
    }));
  };

  const activeSubjects = subjects.filter(subject =>
    Boolean(subject.is_active) === true &&
    (subject.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
     subject.summary.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  const archivedSubjects = subjects.filter(subject =>
    Boolean(subject.is_active) === false &&
    (subject.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
     subject.summary.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  const filteredSubjects = activeTab === 'active' ? activeSubjects : archivedSubjects;

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (user?.role !== 'curator') {
    return (
      <div className="min-h-[100svh] w-full bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-100/30 dark:from-gray-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Only curators can manage subjects.
          </p>
          <Link
            to="/dashboard"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100svh] w-full bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-100/30 dark:from-gray-900 dark:via-slate-800 dark:to-slate-900">
      <PageHeader
        subtitle="Create and manage learning subjects"
        icon={BookOpen}
        iconColor="text-green-600 dark:text-green-400"
        iconBgColor="bg-green-100 dark:bg-green-900/20"
        showBackButton={false}
        rightContent={
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center justify-center p-2 border border-transparent rounded-lg shadow-sm text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200 transform hover:scale-105"
            title="Create Subject"
          >
            <Plus className="h-5 w-5" />
          </button>
        }
        // Search functionality
        showSearch={true}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        searchPlaceholder="Search subjects..."
        // Tab functionality
        showTabs={user?.role === 'curator'}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      <div 
        className="w-full py-3 sm:pb-3 overflow-y-auto h-[100svh]"
        style={{
          paddingTop: 'var(--header-height, 80px)'
        }}
      >
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 dark:border-gray-700 border-t-green-600 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 dark:text-gray-400 font-medium">Loading subjects...</p>
          </div>
        ) : filteredSubjects.length === 0 ? (
          <div className="text-center py-16">
            <div className="bg-white/60 dark:bg-gray-800/60 rounded-3xl p-8 border border-gray-200/50 dark:border-gray-700/50 max-w-md mx-auto">
              <BookOpen className="h-20 w-20 text-gray-400 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                {searchTerm ? 'No subjects found' : 'No subjects created yet'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-8 text-lg">
                {searchTerm ? 'Try adjusting your search terms' : 'Create your first learning subject to get started!'}
              </p>
              {!searchTerm && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center px-6 py-3 border border-transparent rounded-xl shadow-lg text-base font-medium text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200 transform hover:scale-105"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Create First Subject
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden w-full"
            style={{width: '100%', maxWidth: '100%'}}
          >
            {filteredSubjects.map((subject) => (
              <div
                key={subject.id}
                className="flex items-center px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0 w-full min-w-0"
                style={{width: '100%', maxWidth: '100%'}}
                onClick={() => openEditModal(subject)}
              >

                {/* Subject Content */}
                <div className="flex-1 min-w-0 pr-2 overflow-hidden"
                  style={{minWidth: 0, maxWidth: 'calc(100% - 80px)'}}
                >
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate flex-1 min-w-0">
                      {subject.name}
                    </h3>
                  </div>
                  
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1 overflow-hidden">
                    <span className="truncate block w-full">{subject.summary || 'No description provided'}</span>
                  </div>
                  
                  <div className="flex items-center text-xs text-gray-500 dark:text-gray-500 space-x-2 overflow-hidden">
                    <span className="truncate flex-1 min-w-0">{subject.creator_name}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex-shrink-0 mr-3 ml-auto">
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExportSubject(subject);
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      title="Export Subject as CSV"
                    >
                      <Save className="h-4 w-4" />
                    </button>
                    {Boolean(subject.is_active) ? (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArchiveSubject(subject.id);
                          }}
                          className="p-2 text-orange-600 hover:bg-orange-100 dark:hover:bg-orange-900/20 rounded-lg transition-colors"
                          title="Archive Subject"
                        >
                          <Archive className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUnarchiveSubject(subject.id);
                          }}
                          className="p-2 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                          title="Unarchive Subject"
                        >
                          <ArchiveRestore className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirm(subject.id);
                          }}
                          className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Delete Subject"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}


      </div>

      {/* Create/Edit Subject Modal */}
      {(showCreateModal || editingSubject) && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-200/50 dark:border-gray-700/50">
            <div className="p-6 border-b border-gray-200/50 dark:border-gray-700/50">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {editingSubject ? 'Edit Subject' : 'Create New Subject'}
                </h2>
                <div className="flex items-center space-x-2">
                  {!editingSubject && (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,text/csv"
                        onChange={handleImportFileChange}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (!importing && fileInputRef.current) {
                            fileInputRef.current.click();
                          }
                        }}
                        disabled={importing}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white transition-colors"
                        title="Import CSV to populate form"
                      >
                        {importing ? 'Importing…' : 'Import CSV'}
                      </button>
                    </>
                  )}
                  <button
                    onClick={closeModals}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
            
            <form onSubmit={editingSubject ? handleUpdateSubject : handleCreateSubject} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Subject Name *
                </label>
                <input
                  type="text"
                  required
                  value={createForm.name}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-500/20 focus:border-green-500 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-900 dark:text-white transition-all duration-200"
                  placeholder="Enter subject name (e.g., Mathematics, Physics)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Summary *
                </label>
                <textarea
                  required
                  rows={3}
                  value={createForm.summary}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, summary: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-500/20 focus:border-green-500 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-900 dark:text-white resize-none transition-all duration-200"
                  placeholder="Brief description of the subject and what students will learn"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Knowledge Base Prompt
                </label>
                <div className="mb-3">
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <Brain className="h-4 w-4 mr-2" />
                    <span>AI-powered learning assistant configuration</span>
                  </div>
                </div>
                <textarea
                  rows={4}
                  value={createForm.knowledgeBasePrompt}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, knowledgeBasePrompt: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-500/20 focus:border-green-500 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-900 dark:text-white resize-none transition-all duration-200"
                  placeholder="Instructions for AI to help students learn this subject (optional)"
                />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  This helps the AI understand how to assist students with questions about this subject.
                </p>
              </div>

              {/* Stages and Questions Section */}
              <div className="border-t border-gray-200/50 dark:border-gray-700/50 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Learning Stages & Questions
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      const newStage = {
                        tempId: `temp-stage-${tempStageCounter}`,
                        name: '',
                        completion_score_threshold: 70,
                        stage_order: tempStages.length,
                        questions: []
                      };
                      setTempStages(prev => [...prev, newStage]);
                      setTempStageCounter(prev => prev + 1);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Stage
                  </button>
                </div>

                {tempStages.length === 0 && (
                  <div className="text-center py-8 bg-gray-50/50 dark:bg-gray-700/50 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600">
                    <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      No stages added yet. Add learning stages to organize your content.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        const newStage = {
                          tempId: `temp-stage-${tempStageCounter}`,
                          name: '',
                          completion_score_threshold: 70,
                          stage_order: 0,
                          questions: []
                        };
                        setTempStages([newStage]);
                        setTempStageCounter(prev => prev + 1);
                      }}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Stage
                    </button>
                  </div>
                )}

                {tempStages.map((stage, stageIndex) => (
                  <div key={stage.tempId} className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-xl border border-gray-200/50 dark:border-gray-600/50 p-6 mb-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                        Stage {stageIndex + 1}
                      </h4>
                                           <button
                       type="button"
                       onClick={() => {
                         const stage = tempStages[stageIndex];
                         if (stage && stage.tempId.startsWith('existing-stage-')) {
                           setPendingTempRemoval({ type: 'stage', stageIndex, name: stage.name });
                         } else if (stage) {
                           removeTempStage(stageIndex);
                         }
                       }}
                       className="p-2 text-red-400 hover:text-red-600 dark:hover:text-red-300 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200"
                     >
                       <Trash2 className="h-4 w-4" />
                     </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Stage Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={stage.name}
                          onChange={(e) => setTempStages(prev => prev.map((s, index) => 
                            index === stageIndex ? { ...s, name: e.target.value } : s
                          ))}
                          className="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-900 dark:text-white transition-all duration-200"
                          placeholder="Enter stage name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Completion Threshold (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          required
                          value={stage.completion_score_threshold}
                          onChange={(e) => setTempStages(prev => prev.map((s, index) => 
                            index === stageIndex ? { ...s, completion_score_threshold: parseInt(e.target.value) } : s
                          ))}
                          className="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-900 dark:text-white transition-all duration-200"
                        />
                      </div>
                    </div>

                    {/* Questions for this stage */}
                    <div className="border-t border-gray-200/50 dark:border-gray-600/50 pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Questions ({stage.questions.length})
                        </h5>
                                                 <button
                           type="button"
                           onClick={() => {
                             const newQuestion = {
                               tempId: `temp-question-${tempQuestionCounter}`,
                               questionText: '',
                               answer: { tempId: `temp-answer-${tempAnswerCounter}`, text: '', isCorrect: true }
                             };
                             setTempStages(prev => prev.map((s, index) => 
                               index === stageIndex ? { ...s, questions: [...s.questions, newQuestion] } : s
                             ));
                             setTempQuestionCounter(prev => prev + 1);
                             setTempAnswerCounter(prev => prev + 1);
                           }}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-lg text-xs font-medium text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add Question
                        </button>
                      </div>

                      {stage.questions.map((question, questionIndex) => (
                        <div key={question.tempId} className="bg-gray-50/50 dark:bg-gray-700/50 rounded-lg border border-gray-200/50 dark:border-gray-600/50 p-4 mb-3">
                          <div className="flex items-center justify-between mb-3">
                            <h6 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Question {questionIndex + 1}
                            </h6>
                                                         <button
                               type="button"
                               onClick={() => {
                                 const question = tempStages[stageIndex]?.questions[questionIndex];
                                 if (question && question.tempId.startsWith('existing-question-')) {
                                   setPendingTempRemoval({ type: 'question', stageIndex, questionIndex });
                                 } else if (question) {
                                   removeTempQuestion(stageIndex, questionIndex);
                                 }
                               }}
                               className="p-1 text-red-400 hover:text-red-600 dark:hover:text-red-300 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200"
                             >
                               <Trash2 className="h-3 w-3" />
                             </button>
                          </div>

                          <div className="mb-3">
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Question Text *
                            </label>
                            <textarea
                              required
                              rows={2}
                              value={question.questionText}
                              onChange={(e) => setTempStages(prev => prev.map((s, sIndex) => 
                                sIndex === stageIndex ? {
                                  ...s,
                                  questions: s.questions.map((q, qIndex) => 
                                    qIndex === questionIndex ? { ...q, questionText: e.target.value } : q
                                  )
                                } : s
                              ))}
                              className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-900 dark:text-white resize-none transition-all duration-200 ${
                                (question.questionText?.length || 0) > 0 && (question.questionText?.length || 0) < 3 
                                  ? 'border-red-300 dark:border-red-600' 
                                  : 'border-gray-200 dark:border-gray-600'
                              }`}
                              placeholder="Enter your question (minimum 3 characters)"
                            />
                            {(question.questionText?.length || 0) > 0 && (question.questionText?.length || 0) < 3 && (
                              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                                Question must be at least 3 characters long
                              </p>
                            )}
                          </div>

                          <div className="mb-3">
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Time Limit (seconds) *
                            </label>
                            <input
                              type="number"
                              min="10"
                              max="3600"
                              required
                              value={question.timeLimitSeconds ?? 120}
                              onChange={(e) => setTempStages(prev => prev.map((s, sIndex) => 
                                sIndex === stageIndex ? {
                                  ...s,
                                  questions: s.questions.map((q, qIndex) => 
                                    qIndex === questionIndex ? { ...q, timeLimitSeconds: parseInt(e.target.value) || 120 } : q
                                  )
                                } : s
                              ))}
                              className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-900 dark:text-white transition-all duration-200 border-gray-200 dark:border-gray-600"
                              placeholder="120"
                            />
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              Default: 120 seconds (2 minutes). Range: 10-3600 seconds.
                            </p>
                          </div>

                                                     <div className="space-y-2">
                             <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                               Answer *
                             </label>
                             <div className="flex items-center space-x-2">
                               <input
                                 type="text"
                                 required
                                 value={question.answer.text}
                                 onChange={(e) => setTempStages(prev => prev.map((s, sIndex) => 
                                   sIndex === stageIndex ? {
                                     ...s,
                                     questions: s.questions.map((q, qIndex) => 
                                       qIndex === questionIndex ? { ...q, answer: { ...q.answer, text: e.target.value } } : q
                                     )
                                   } : s
                                 ))}
                                 className={`flex-1 px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-900 dark:text-white transition-all duration-200 ${
                                   (question.answer?.text?.length || 0) > 0 && (question.answer?.text?.length || 0) < 1 
                                     ? 'border-red-300 dark:border-red-600' 
                                     : 'border-gray-200 dark:border-gray-600'
                                 }`}
                                 placeholder="Enter the correct answer (minimum 1 character)"
                               />
                             </div>
                             {(question.answer?.text?.length || 0) === 0 && (
                               <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                                 Answer cannot be empty
                               </p>
                             )}
                           </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex space-x-4 pt-6">
                <button
                  type="button"
                  onClick={closeModals}
                  className="flex-1 px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 border border-transparent rounded-xl text-sm font-medium text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200 transform hover:scale-105"
                >
                  <div className="flex items-center justify-center">
                    <Save className="h-4 w-4 mr-2" />
                    {editingSubject ? 'Update Subject' : 'Create Subject'} 
                  </div>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-2xl shadow-2xl max-w-md w-full border border-gray-200/50 dark:border-gray-700/50">
            <div className="p-6 border-b border-gray-200/50 dark:border-gray-700/50">
              <div className="flex items-center">
                <AlertTriangle className="h-6 w-6 text-red-500 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Delete Subject
                </h3>
              </div>
            </div>
            
            <div className="p-6">
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to delete this subject? This action cannot be undone and will remove all associated stages, questions, and answers.
              </p>
              
              <div className="flex space-x-4">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200">
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteSubject(deleteConfirm)}
                  className="flex-1 px-4 py-3 border border-transparent rounded-xl text-sm font-medium text-white bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200 transform hover:scale-105">
                  <div className="flex items-center justify-center">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Subject
                  </div>

                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={Boolean(pendingTempRemoval)}
        title={pendingTempRemoval?.type === 'stage' ? 'Remove Stage' : 'Remove Question'}
        message={
          pendingTempRemoval?.type === 'stage' ? (
            <>
              Remove stage <span className="font-medium">{pendingTempRemoval.name}</span> and all of its questions from this draft?
            </>
          ) : (
            'Remove this question from this draft?'
          )
        }
        confirmLabel="Remove"
        variant="danger"
        onCancel={() => setPendingTempRemoval(null)}
        onConfirm={confirmTempRemoval}
      />

      {/* Create Stage Modal */}
      {showStageModal && selectedSubject && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-2xl shadow-2xl max-w-lg w-full border border-gray-200/50 dark:border-gray-700/50">
            <div className="p-6 border-b border-gray-200/50 dark:border-gray-700/50">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Add Stage to {selectedSubject.name}
                </h2>
                <button
                  onClick={closeStageModal}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleCreateStage} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Stage Name *
                </label>
                <input
                  type="text"
                  required
                  value={createStageForm.name}
                  onChange={(e) => setCreateStageForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-900 dark:text-white transition-all duration-200"
                  placeholder="Enter stage name (e.g., Introduction, Basic Concepts)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Completion Score Threshold *
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  required
                  value={createStageForm.completionScoreThreshold}
                  onChange={(e) => setCreateStageForm(prev => ({ ...prev, completionScoreThreshold: parseInt(e.target.value) }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-900 dark:text-white transition-all duration-200"
                  placeholder="70"
                />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Minimum score percentage required to complete this stage
                </p>
              </div>

              <div className="flex space-x-4 pt-6">
                <button
                  type="button"
                  onClick={closeStageModal}
                  className="flex-1 px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 border border-transparent rounded-xl text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Stage
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Question Modal */}
      {showQuestionModal && selectedStage && selectedSubject && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-200/50 dark:border-gray-700/50">
            <div className="p-6 border-b border-gray-200/50 dark:border-gray-700/50">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Add Question to {selectedStage.name}
                </h2>
                <button
                  onClick={closeQuestionModal}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleCreateQuestion} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Question Text *
                </label>
                <textarea
                  required
                  rows={3}
                  value={createQuestionForm.questionText}
                  onChange={(e) => setCreateQuestionForm(prev => ({ ...prev, questionText: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-500/20 focus:border-green-500 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-900 dark:text-white resize-none transition-all duration-200"
                  placeholder="Enter your question here..."
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Answers *
                  </label>
                  <button
                    type="button"
                    onClick={addAnswerField}
                    className="inline-flex items-center px-3 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200 transform hover:scale-105"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Answer
                  </button>
                </div>
                
                <div className="space-y-4">
                  {createQuestionForm.answers.map((answer, index) => (
                    <div key={index} className="border-2 border-gray-200/50 dark:border-gray-600/50 rounded-xl p-4 bg-white/60 dark:bg-gray-700/60 backdrop-blur-sm">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                          Answer {index + 1}
                        </span>
                        {createQuestionForm.answers.length > 2 && (
                          <button
                            type="button"
                            onClick={() => removeAnswerField(index)}
                            className="text-red-500 hover:text-red-700 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/20 px-2 py-1 rounded-lg transition-all duration-200"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      
                      <input
                        type="text"
                        required
                        value={answer.text}
                        onChange={(e) => updateAnswer(index, 'text', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-500/20 focus:border-green-500 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-900 dark:text-white mb-3 transition-all duration-200"
                        placeholder={`Answer ${index + 1} text`}
                      />
                      
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center cursor-pointer group">
                          <input
                            type="radio"
                            name="correctAnswer"
                            checked={answer.isCorrect}
                            onChange={() => {
                              setCreateQuestionForm(prev => ({
                                ...prev,
                                answers: prev.answers.map((a, i) => ({
                                  ...a,
                                  isCorrect: i === index
                                }))
                              }));
                            }}
                            className="mr-3 text-green-600 focus:ring-4 focus:ring-green-500/20 h-4 w-4"
                          />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-green-600 transition-colors duration-200">Correct Answer</span>
                        </label>
                      </div>
                      
                      <input
                        type="text"
                        value={answer.explanation}
                        onChange={(e) => updateAnswer(index, 'explanation', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-500/20 focus:border-green-500 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-900 dark:text-white mt-3 transition-all duration-200"
                        placeholder="Explanation (optional)"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex space-x-4 pt-6">
                <button
                  type="button"
                  onClick={closeQuestionModal}
                  className="flex-1 px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 border border-transparent rounded-xl text-sm font-medium text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200 transform hover:scale-105"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Question
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubjectEditorPage;


